import {
  downloadCSV,
  ListActionsProps,
  sanitizeListRestProps,
  TopToolbar,
  useListContext,
  useNotify,
} from 'react-admin';
import CustomExportButton from 'components/CustomExportButton';
import {
  buildCostStructureHistoryCsv,
  CsvColumn,
} from 'utils/costStructureHistoryCsv';

const EXPORT_FAILED_MESSAGE = 'Export failed. Please try again.';

export type CostStructureHistoryActionsProps<Row> = ListActionsProps & {
  /** Region or Subdivision name for the file name. */
  name?: string;
  /** Stands in while `name` is still loading. */
  nameFallback: string;
  loading?: boolean;
  /** In on-screen order. */
  columns: CsvColumn<Row>[];
  'data-cy'?: string;
};

/**
 * `CustomExportButton` re-fetches the list with the screen's filter and sort,
 * so the file holds exactly the rows on screen.
 */
export const CostStructureHistoryActions = <Row,>({
  className,
  name,
  nameFallback,
  loading,
  columns,
  'data-cy': dataCy,
  ...rest
}: CostStructureHistoryActionsProps<Row>) => {
  const { loaded, loading: listLoading } = useListContext();
  const notify = useNotify();

  const exporter = (rows: Row[]) => {
    try {
      const { filename, csv } = buildCostStructureHistoryCsv({
        name: name ?? nameFallback,
        rows,
        columns,
      });
      downloadCSV(csv, filename);
    } catch (error) {
      console.error(error);
      notify(EXPORT_FAILED_MESSAGE, 'error');
    }
  };

  return (
    <TopToolbar className={className} {...sanitizeListRestProps(rest)}>
      <CustomExportButton
        label="Export"
        exporter={exporter}
        disabled={loading || listLoading || !loaded}
        data-cy={dataCy}
      />
    </TopToolbar>
  );
};

export default CostStructureHistoryActions;
